import type { ActionReducerMapBuilder, PayloadAction } from "@reduxjs/toolkit";
import { uniqBy } from "lodash";
import { categorizeByDate } from "src/helpers/utils";
import type {
  JobData,
  JobDetailsState,
  ResidentCaused,
  WorkOrderFlag,
} from "src/types/jobs";

import {
  deleteResidentResponsibility,
  fetchJobActivityLog,
  fetchJobDetails,
  fetchMessages,
  fetchMessageThreads,
  fetchServiceRequestDetails,
  fetchWorkOrderFlags,
  getJobNotes,
  getServiceRequestNotes,
  markResidentResponsibility,
  sendMessage,
  updateResidentResponsibility,
} from "./api";


// Raw JSON body returned by the work-order flag rollup endpoint. Every field is
// optional so the guard below stays tolerant of an undefined / partial payload —
// mirrors the runtime guard the flag-summary slice applies to its own payload.
interface WorkOrderFlagRollupPayload {
  work_order_job_id?: number | string;
  has_active_flag?: boolean;
  flags?: unknown;
}

// Runtime guard: coerce the raw payload into the typed rollup shape (or null) so a
// malformed / partial response can never silently mistype `flagRollup` state. A
// non-object payload yields null; a non-array `flags` yields an empty list.
const normalizeFlagRollup = (
  payload: unknown,
): JobDetailsState["flagRollup"] => {
  if (!payload || typeof payload !== "object") {
    return null;
  }
  const raw = payload as WorkOrderFlagRollupPayload;
  return {
    work_order_job_id: Number(raw.work_order_job_id ?? 0),
    has_active_flag: !!raw.has_active_flag,
    flags: Array.isArray(raw.flags) ? (raw.flags as WorkOrderFlag[]) : [],
  };
};

interface ResidentCausedResponse extends ResidentCaused {
  message: string,
}

const extraReducers = (
  builder: ActionReducerMapBuilder<JobDetailsState>,
): void => {
  // get job details
  builder.addCase(fetchJobDetails().pending, (state) => {
    if (!state.slideOut.needToRefetch) {
      state.isLoading = true;
    }
  });
  builder.addCase(fetchJobDetails().fulfilled, (state, action) => {
    state.isLoading = false;
    state.data = action?.payload as JobDetailsState["data"];
  });
  builder.addCase(fetchJobDetails().rejected, (state) => {
    state.isLoading = false;
  });

  // get SR details
  builder.addCase(fetchServiceRequestDetails().pending, (state) => {
    if (state.slideOut.needToRefetch !== true) {
      state.isLoading = true;
    }
  });
  builder.addCase(fetchServiceRequestDetails().fulfilled, (state, action) => {
    state.isLoading = false;
    state.data = action?.payload as JobDetailsState["data"];
  });
  builder.addCase(fetchServiceRequestDetails().rejected, (state) => {
    state.isLoading = false;
  });

  // get work order flag rollup (Build Back Risk customer surfaces)
  builder.addCase(fetchWorkOrderFlags().pending, (state) => {
    state.isFlagRollupLoading = true;
  });
  builder.addCase(fetchWorkOrderFlags().fulfilled, (state, action) => {
    state.isFlagRollupLoading = false;
    state.flagRollup = normalizeFlagRollup(action?.payload);
  });
  builder.addCase(fetchWorkOrderFlags().rejected, (state) => {
    state.isFlagRollupLoading = false;
  });

  builder.addCase(fetchJobActivityLog().pending, (state) => {
    state.isActivityLogLoading = true;
  });
  builder.addCase(fetchJobActivityLog().fulfilled, (state, action) => {
    const payload = action.payload as JobDetailsState["activityLog"];
    const data = payload.data;
    const meta = payload.meta;
    const links = payload.links;
    const activityLog = state.activityLog;
    const activityLogData = activityLog.data;


    if (meta?.prev_cursor) {
      state.activityLog = {
        ...activityLog,
        data: [...(activityLogData || []), ...(data || [])],
        meta,
        links,
      };
    } else {
      state.activityLog = {
        data: [...(data || [])],
        meta,
        links,
      };
    }
    state.isActivityLogLoading = false;
  });

  builder.addCase(fetchJobActivityLog().rejected, (state) => {
    state.isActivityLogLoading = false;
  });

  // get job notes

  builder.addCase(getJobNotes().pending, (state) => {
    state.isFetchingNotes = true;
  });
  builder.addCase(getJobNotes().fulfilled, (state, action) => {
    state.isFetchingNotes = false;
    const payload = action?.payload as { data: JobData["notes"] };
    state.data.notes = payload?.data;
  });
  builder.addCase(getJobNotes().rejected, (state) => {
    state.isFetchingNotes = false;
  });

  // get SR notes
  builder.addCase(getServiceRequestNotes().pending, (state) => {
    state.isFetchingNotes = true;
  });
  builder.addCase(getServiceRequestNotes().fulfilled, (state, action) => {
    state.isFetchingNotes = false;
    const payload = action?.payload as { data: JobData["notes"] };
    state.data.notes = payload?.data;
  });
  builder.addCase(getServiceRequestNotes().rejected, (state) => {
    state.isFetchingNotes = false;
  });

  // FETCH MESSAGES
  builder.addCase(fetchMessages().pending, (state) => {
    state.isFetchingMessages = true;
  });

  builder.addCase(fetchMessages().fulfilled, (state, action) => {
    const payload = action.payload as Record<string, unknown>;
    const data = payload.data as Array<Record<string, unknown>>;
    const meta = payload.meta as Record<string, unknown>;
    const links = payload.links as Record<string, unknown>;
    const page = (action?.meta?.arg as Record<string, unknown>)?.page as
      | number
      | undefined;

    const formattedData = categorizeByDate(
      data,
      (state?.data as Record<string, unknown>)?.timezone as string | undefined,
    );

    let newMessageData: Record<string, unknown> = {};
    if (page && page > 1) {
      const messagesData = (state?.messages as Record<string, unknown>)
        ?.data as Record<string, Record<string, unknown>> | undefined;
      const newDataList: Record<string, Record<string, unknown>> = {
        ...(messagesData || {}),
      };
      Object?.keys(formattedData || {})?.forEach((key) => {
        const newMessages = [
          ...((newDataList[key]?.content as Array<Record<string, unknown>>) ||
            []),
          ...((formattedData[key]?.content as Array<Record<string, unknown>>) ||
            []),
        ];
        newDataList[key] = {
          ...(newDataList[key] || {}),
          value: formattedData?.[key]?.value,
          content: uniqBy(newMessages, "message_id"),
        };
      });

      newMessageData = {
        data: { ...newDataList },
        meta,
        links,
      };
    } else {
      const extras = meta?.extras as Record<string, unknown> | undefined;
      (state.messages as Record<string, unknown>).unread_chat_ids =
        extras?.unread_chat_ids || [];
      (state.messages as Record<string, unknown>).isChatDisabled = {
        isChatDisabled: extras?.is_chat_disabled || false,
        chatDisabledReason: extras?.disabled_reason || "",
      };
      newMessageData = {
        data: formattedData,
        meta,
        links,
      };
    }
    state.messages = {
      ...(state.messages || {}),
      ...(newMessageData || {}),
    } as JobDetailsState["messages"];

    state.isFetchingMessages = false;
  });
  builder.addCase(fetchMessages().rejected, (state) => {
    state.isFetchingMessages = false;
  });

  builder.addCase(fetchMessageThreads().pending, (state) => {
    state.isFetchingMessageThreads = true;
  });
  builder.addCase(fetchMessageThreads().fulfilled, (state, action) => {
    (state.messages as Record<string, unknown>).threads = action?.payload;
    state.isFetchingMessageThreads = false;
    (state.messages as Record<string, unknown>).shouldRefetchGroups = false;
  });
  builder.addCase(fetchMessageThreads().rejected, (state) => {
    state.isFetchingMessageThreads = false;
  });

  builder.addCase(sendMessage().fulfilled, (state, action) => {
    const threads = (state.messages as Record<string, unknown>)?.threads as
      | Array<Record<string, unknown>>
      | undefined;
    (state.messages as Record<string, unknown>).threads = threads?.map(
      (thread) => {
        const arg = action?.meta?.arg as Record<string, unknown> | undefined;
        if (thread?.customer_chat_group_id === arg?.activeThreadId) {
          return {
            ...thread,
            last_message_at: (action?.payload as Record<string, unknown>)
              ?.created_at,
            // Replying marks every unread admin/vendor message in the thread
            // read on the backend (readAllUnReadMessages); mirror that here.
            unread_chat_count: 0,
          };
        }
        return thread;
      },
    );
    (state.messages as Record<string, unknown>).unread_chat_ids = [];
  });

  // mark resident responsibility
  builder.addCase(markResidentResponsibility().pending, (state) => {
    state.updatingResidentCaused = true;
  });
  builder.addCase(markResidentResponsibility().fulfilled, (state, action) => {
    const {message, ...data} = action.payload as ResidentCausedResponse;
    state.data.resident_caused = [data];
    state.updatingResidentCaused = false;
  });
  builder.addCase(markResidentResponsibility().rejected, (state) => {
    state.updatingResidentCaused = false;
  });

  // update resident responsibility
  builder.addCase(updateResidentResponsibility().pending, (state) => {
    state.updatingResidentCaused = true;
  });
  builder.addCase(updateResidentResponsibility().fulfilled, (state, action) => {
    const {message, ...data} = action.payload as ResidentCausedResponse;
    if (state.data.resident_caused) {
      const index = state.data.resident_caused.findIndex((rc) => rc.job_id === data.job_id);
      if (index !== -1) {
        state.data.resident_caused[index] = data;
      } else {
        state.data.resident_caused.push(data);
      }
    } else {
      state.data.resident_caused = [data];
    }
    state.updatingResidentCaused = false;
  });
  builder.addCase(updateResidentResponsibility().rejected, (state) => {
    state.updatingResidentCaused = false;
  });

  // delete resident responsibility
  builder.addCase(deleteResidentResponsibility().pending, (state) => {
    state.updatingResidentCaused = true;
  });
  builder.addCase(deleteResidentResponsibility().fulfilled, (state, action) => {
    const jobId = (action.payload as { jobId: number })?.jobId;
    if (state.data.resident_caused && jobId !== undefined) {
      const filtered = state.data.resident_caused.filter((rc) => rc.job_id !== jobId);
      state.data.resident_caused = filtered.length > 0 ? filtered : null;
    } else {
      state.data.resident_caused = null;
    }
    state.updatingResidentCaused = false;
  });
  builder.addCase(deleteResidentResponsibility().rejected, (state) => {
    state.updatingResidentCaused = false;
  });
};

export default extraReducers;
